import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useOrgId from "@/components/FpAppV4/hooks/useOrgId";
import { fetchDrives, type Drive } from "./fetchFiles";

interface UseDriveSelectorOptions {
  /**
   * Whether to read the drive list. A closed picker passes false, so it loads
   * nothing until it first opens (#12239). Defaults to true.
   */
  enabled?: boolean;
}

interface UseDriveSelectorResult {
  driveSelector: React.ReactNode;
  selectedDriveId: string | undefined;
  showAcrossDrives: boolean;
  drives: Drive[];
  drivesEnabled: boolean;
  /** The drive list is being read and has not answered yet. */
  loading: boolean;
  /** Pick a drive by id, or "across-drives"; remembered like the selector. */
  selectDrive: (selection: string) => void;
}

const STORAGE_KEY_PREFIX = "fp-selected-drive-";
const NO_DRIVES: Drive[] = [];

/**
 * The drive list's query key. Every selector mounted on a page shares the one
 * read (#12239: a chat page read `/users/drives` two or three times). The
 * query keeps `staleTime: 0`, so each new mount still reads the list fresh.
 */
export const drivesQueryKey = (orgId: string | undefined) =>
  ["users", "drives", orgId] as const;

export const useDriveSelector = ({
  enabled = true,
}: UseDriveSelectorOptions = {}): UseDriveSelectorResult => {
  const orgId = useOrgId();
  const query = useQuery({
    queryKey: drivesQueryKey(orgId),
    queryFn: fetchDrives,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
    enabled,
  });
  const data = query.data;
  const drives = data?.drives ?? NO_DRIVES;
  const drivesEnabled = data?.drivesEnabled ?? false;
  // A disabled query is pending too, but nothing is being read.
  const loading = query.isPending && query.fetchStatus !== "idle";
  const error: string | null =
    query.error === null
      ? null
      : query.error instanceof Error
        ? query.error.message
        : "Failed to load drives";

  // Selection state: either a drive ID or "across-drives"
  const [selection, setSelection] = useState<string>("across-drives");

  // Restore the remembered selection when the list answers. Structural
  // sharing keeps the data's identity across a refetch that changed nothing,
  // so an unchanged list does not run this again.
  useEffect(() => {
    if (!data) return;
    if (data.drivesEnabled && data.drives.length > 0) {
      // Restore saved drive selection if it still exists
      const saved = orgId
        ? localStorage.getItem(STORAGE_KEY_PREFIX + orgId)
        : null;
      if (
        saved !== null &&
        (saved === "across-drives" || data.drives.some((d) => d._id === saved))
      ) {
        setSelection(saved);
      } else {
        setSelection(data.drives[0]._id);
      }
    } else {
      // Org without drives — use no-drive mode (not across-drives)
      setSelection("no-drives");
    }
  }, [data, orgId]);

  useEffect(() => {
    if (query.error) console.error("Failed to fetch drives:", query.error);
  }, [query.error]);

  // Derive the values consumers need
  const selectedDriveId =
    selection === "across-drives" || selection === "no-drives"
      ? undefined
      : selection;
  const showAcrossDrives = selection === "across-drives";

  let driveSelector: React.ReactNode = null;

  if (loading) {
    driveSelector = (
      <div className="flex items-center gap-2">
        <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full" />
        <span className="text-sm text-gray-600">Loading drives...</span>
      </div>
    );
  } else if (error) {
    driveSelector = (
      <div className="text-sm text-red-600">Error loading drives: {error}</div>
    );
  } else if (drivesEnabled && drives.length > 0) {
    driveSelector = (
      <div className="flex items-center gap-2">
        <label
          htmlFor="drive-select"
          className="text-sm font-medium text-gray-700"
        >
          Drive:
        </label>
        <select
          id="drive-select"
          value={selection}
          onChange={(e) => {
            const value = e.target.value;
            setSelection(value);
            if (orgId) {
              localStorage.setItem(STORAGE_KEY_PREFIX + orgId, value);
            }
          }}
          className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="across-drives">Across Drives</option>
          {drives.map((drive) => (
            <option key={drive._id} value={drive._id}>
              {drive.name}
            </option>
          ))}
        </select>
      </div>
    );
  }

  const selectDrive = (next: string) => {
    setSelection(next);
    if (orgId) localStorage.setItem(STORAGE_KEY_PREFIX + orgId, next);
  };

  return {
    driveSelector,
    selectedDriveId,
    showAcrossDrives,
    drives,
    drivesEnabled,
    loading,
    selectDrive,
  };
};
